import { MoreVert, Send } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { TextField } from '@side/pantry';
import { useState } from 'react';
import type { ReportingInsightsStarterPrompt } from './AIInsightsButton.helpers';

export const AIInsightsControls = ({
  isBusy,
  starterPrompts,
  inputMessage,
  canSendMessage,
  onInputMessageChange,
  onRunPrompt,
  onSendFollowUp,
  onClearChat,
}: {
  isBusy: boolean;
  starterPrompts: ReportingInsightsStarterPrompt[];
  inputMessage: string;
  canSendMessage: boolean;
  onInputMessageChange: (value: string) => void;
  onRunPrompt: (
    promptId: ReportingInsightsStarterPrompt['id'],
    label: string,
  ) => void;
  onSendFollowUp: () => void;
  onClearChat: () => void;
}) => {
  const CONTROL_HEIGHT_PX = 46;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  return (
    <Stack direction="row" sx={{ p: 2, gap: 1, alignItems: 'center' }}>
      <IconButton
        aria-label="Insight options"
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
        disabled={isBusy}
        sx={(theme) => ({
          color: 'common.white',
          border: '1px solid',
          borderColor: theme.alpha(theme.palette.common.white, 0.35),
          borderRadius: '4px',
          width: CONTROL_HEIGHT_PX,
          height: CONTROL_HEIGHT_PX,
          '&:hover': {
            borderColor: theme.alpha(theme.palette.common.white, 0.5),
            backgroundColor: theme.alpha(theme.palette.common.white, 0.08),
          },
        })}
      >
        <MoreVert />
      </IconButton>
      <Menu
        open={isMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            onClearChat();
          }}
        >
          Clear chat
        </MenuItem>
        {starterPrompts.map((prompt) => (
          <MenuItem
            key={prompt.id}
            onClick={() => {
              setMenuAnchorEl(null);
              onRunPrompt(prompt.id, prompt.label);
            }}
          >
            {prompt.label}
          </MenuItem>
        ))}
      </Menu>
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={5}
        value={inputMessage}
        placeholder="Ask anything"
        onChange={(event) => onInputMessageChange(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === 'Enter' &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            onSendFollowUp();
          }
        }}
        disabled={isBusy}
        variant="outlined"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ mt: 'auto' }}>
                <IconButton
                  aria-label="Send insight message"
                  color="secondary"
                  size="small"
                  onClick={onSendFollowUp}
                  disabled={!canSendMessage}
                  sx={(theme) => ({
                    backgroundColor: canSendMessage
                      ? 'secondary.light'
                      : 'transparent',
                    alignSelf: 'flex-end',
                    mr: 1.5,
                    my: 1,
                    color: canSendMessage ? 'secondary.main' : 'common.white',
                    '&:hover': {
                      backgroundColor: canSendMessage
                        ? theme.darken(theme.palette.secondary.light, 0.1)
                        : 'transparent',
                      color: canSendMessage ? undefined : 'secondary.200',
                    },
                  })}
                >
                  <Send fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: (theme) => ({
              borderRadius: '4px',
              minHeight: CONTROL_HEIGHT_PX,
              backgroundColor: theme.lighten(
                theme.palette.other.spaceCadet,
                0.05,
              ),
              color: 'common.white',
            }),
          },
        }}
        sx={(theme) => ({
          '& .MuiOutlinedInput-input': {
            py: 1,
            px: 1.5,
            color: 'common.white',
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.common.white} !important`,
          },
        })}
      />
    </Stack>
  );
};
